import apiClient from './axios-instance'
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  SignupRequest,
  SignupResponse,
  User,
} from '@/lib/types/auth.types'

/**
 * Auth API Service
 * All authentication-related API calls
 */
export const authApi = {
  /**
   * Signup - Register a new educator account
   */
  signup: async (data: SignupRequest): Promise<ApiResponse<SignupResponse>> => {
    const response = await apiClient.post<ApiResponse<SignupResponse>>(
      '/auth/educator/signup',
      data
    )
    return response.data
  },

  /**
   * Login - Authenticate educator
   */
  login: async (data: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>(
      '/auth/educator/login',
      data
    )
    return response.data
  },

  /**
   * Logout - Clear session (client-side for now)
   */
  logout: async (): Promise<ApiResponse<any>> => {
    const response = await apiClient.post('/auth/educator/logout')

    // Clear local storage
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')

    // Clear external chat session on logout
    localStorage.removeItem('external_chat_session')
    localStorage.removeItem('external_chat_metadata')

    return response.data
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    const response = await apiClient.get<ApiResponse<User>>('/auth/me')
    return response.data
  },

  /**
   * Verify email using user_id and otp
   */
  verifyEmail: async (
    user_id: string,
    otp: string
  ): Promise<ApiResponse<any>> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/auth/verify-email',
      { user_id, otp }
    )
    return response.data
  },

  /**
   * Resend verification OTP to user's email
   */
  resendVerificationOtp: async (
    user_id: string,
    email: string
  ): Promise<ApiResponse<any>> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/auth/resend-verification-otp',
      { user_id, email }
    )
    return response.data
  },

  /**
   * Send password reset link to educator's email
   */
  forgotPassword: async (email: string): Promise<ApiResponse<any>> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/auth/educator/password-reset/send-reset-link',
      { email }
    )
    return response.data
  },

  /**
   * Reset educator password using token from email
   */
  resetPassword: async (
    reset_token: string,
    new_password: string
  ): Promise<ApiResponse<any>> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/auth/educator/password-reset/reset-password',
      {
        reset_token,
        new_password,
      }
    )
    return response.data
  },

  /**
   * Save Admin FCM Token
   */
  saveAdminFCMToken: async (token: string): Promise<ApiResponse<any>> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/admin/settings/save-fcm-token',
      { fcm_token: token }
    )
    return response.data
  },

  /**
   * Save Educator FCM Token
   */
  saveEducatorFCMToken: async (token: string): Promise<ApiResponse<any>> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/educator/save-fcm-token',
      { fcm_token: token }
    )
    return response.data
  },
}

export default authApi
